import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Input,
  Select,
  Form,
  Space,
  Typography,
  Tag,
  Alert,
  Divider,
  Switch,
  InputNumber,
  Tooltip,
  message,
  Modal,
  Table,
  Badge,
  Collapse,
  List,
  Avatar,
  Tabs,
  Steps,
  Slider,
  Radio,
  Progress,
  Statistic,
  Timeline,
  Drawer,
  FloatButton,
  Segmented,
  QRCode,
  Tour,
  TreeSelect,
  Popconfirm,
  Empty,
  Descriptions,
  Transfer,
  AutoComplete,
  Dropdown,
  Menu,
  Checkbox,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  SaveOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  ReloadOutlined,
  BulbOutlined,
  PlayCircleOutlined,
  CodeOutlined,
  ExperimentOutlined,

  RobotOutlined,
  GlobalOutlined,
  ScanOutlined,
  BarChartOutlined,
  HeatMapOutlined,
  CloudOutlined,
  MobileOutlined,
  ShareAltOutlined,
  QuestionCircleOutlined,
  RocketOutlined,
  StarOutlined,
  FireOutlined,
  UserOutlined,
  WarningOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  ApartmentOutlined,
  DatabaseOutlined,
  BoxPlotOutlined,
  BarsOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  SyncOutlined,
  ExportOutlined,
  FileExcelOutlined,
  ImportOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { DataNode } from 'antd/es/tree';
import LocationManagement from '../components/LocationManagement';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import apiService from '../../../services/apiService';
import { useTranslation } from "react-i18next";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;
const { TabPane } = Tabs;
const { Step } = Steps;

// 統一的倉庫接口
interface UnifiedWarehouse {
  id: string;
  name: string;
  code: string;
  type: 'smart' | 'automated' | 'traditional' | 'cold_storage' | 'hazmat';
  location: {
    address: string;
    coordinates: { lat: number; lng: number };
    timezone: string;
    country: string;
  };
  capacity: {
    total: number;
    used: number;
    reserved: number;
    available: number;
    unit: 'cbm' | 'sqm' | 'pallets';
  };
  // 新增：倉庫層級的溫度要求
  temperatureRequirement?: string;
  // 是否為預設倉庫
  isDefault?: boolean;
  zones: WarehouseZone[];
  locationRules: LocationCodingRule[];
  technology: {
    wms: boolean;
    rfid: boolean;
    automation: boolean;
    iot_sensors: boolean;
    robotics: boolean;
  };
  performance: {
    efficiency: number;
    accuracy: number;
    throughput: number;
    costs: number;
  };
  status: 'operational' | 'maintenance' | 'construction' | 'offline';
  created_at: string;
  updated_at: string;
}

// 統一的位置接口
interface WarehouseLocation {
  id: string;
  warehouseId: string;
  code: string;
  name: string;
  type: 'ZONE' | 'AISLE' | 'RACK' | 'SHELF' | 'BIN' | 'PACKING_ZONE';
  parentId?: string;
  level: number;
  capacity?: number;
  usedCapacity: number;
  isActive: boolean;
  coordinates: { x: number; y: number; z?: number };
  children?: WarehouseLocation[];
  parentPath?: string;
  utilizationRate?: number;
  autoGenerated?: boolean;
  ruleId?: string;
  // 新增溫度控制
  temperature?: {
    min: number;
    max: number;
    unit: 'celsius' | 'fahrenheit';
    isControlled: boolean;
  };
}

// 區域接口
interface WarehouseZone {
  id: string;
  name: string;
  type: 'receiving' | 'storage' | 'picking' | 'packing' | 'shipping' | 'quarantine' | 'returns' | 'cross_dock';
  layout: {
    aisles: number;
    levels: number;
    positions_per_level: number;
    aisle_width: number;
    height: number;
  };
  locations: WarehouseLocation[];
  equipment: Equipment[];
  environmental: {
    temperature: { min: number; max: number };
    humidity: { min: number; max: number };
  };
  automation_level: 'manual' | 'semi_automated' | 'fully_automated';
}

// 設備接口
interface Equipment {
  id: string;
  type: 'forklift' | 'conveyor' | 'robot' | 'scanner' | 'scale' | 'printer';
  name: string;
  status: 'active' | 'maintenance' | 'offline';
  location?: string;
}

// 人性化的編碼規則接口
interface LocationCodingRule {
  id: string;
  name: string;
  description: string;
  warehouseId: string;
  isDefault: boolean;
  isActive: boolean;
  
  // 簡化的配置
  pattern: {
    zone: {
      type: 'letter' | 'number' | 'custom';
      format: string; // 如 'A-Z' | '1-9' | 'CUSTOM_LIST'
      customList?: string[]; // 自定義列表如 ['REC', 'STO', 'PCK']
    };
    aisle: {
      type: 'letter' | 'number';
      format: string; // 如 'A-Z' | '01-99'
      length?: number;
    };
    rack: {
      type: 'number';
      format: string; // 如 '001-999'
      length: number;
    };
    shelf: {
      type: 'number';
      format: string; // 如 '01-10'
      length: number;
    };
    bin: {
      type: 'number';
      format: string; // 如 '01-20'
      length: number;
    };
    separator: string; // 分隔符如 '-' | '' | '.'
  };
  
  // 新增溫度配置
  temperature?: {
    zone?: { min: number; max: number; unit: 'celsius' | 'fahrenheit'; isControlled: boolean };
    aisle?: { min: number; max: number; unit: 'celsius' | 'fahrenheit'; isControlled: boolean };
    rack?: { min: number; max: number; unit: 'celsius' | 'fahrenheit'; isControlled: boolean };
    shelf?: { min: number; max: number; unit: 'celsius' | 'fahrenheit'; isControlled: boolean };
    bin?: { min: number; max: number; unit: 'celsius' | 'fahrenheit'; isControlled: boolean };
  };
  
  // 自動生成的示例和驗證
  examples: string[];
  totalPositions: number;
  estimatedCapacity: number;
  created_at: string;
  updated_at: string;
}

const WarehouseManagement: React.FC = () => {
    const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [warehouses, setWarehouses] = useState<UnifiedWarehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<UnifiedWarehouse | null>(null);
  // 由編碼向導或其他操作生成並應用到當前倉庫的臨時位置
  const [generatedLocations, setGeneratedLocations] = useState<WarehouseLocation[] | null>(null);
  const [applyToken, setApplyToken] = useState(0);
  const [locations, setLocations] = useState<WarehouseLocation[]>([]);
  const [codingRules, setCodingRules] = useState<LocationCodingRule[]>([]);
  // 受控分頁（倉庫概覽、編碼管理）
  const [warehousePage, setWarehousePage] = useState(1);
  const [warehousePageSize, setWarehousePageSize] = useState(10);
  const [warehouseTotal, setWarehouseTotal] = useState(0);
  const [codingPage, setCodingPage] = useState(1);
  const [codingPageSize, setCodingPageSize] = useState(10);
  const [codingTotal, setCodingTotal] = useState(0);

  const toInt = (v: unknown): number => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number.parseInt(String(v ?? 0), 10);
    return Number.isFinite(n) ? n : 0;
  };

  const defaultPattern: LocationCodingRule['pattern'] = {
    zone: { type: 'letter', format: 'A-Z' },
    aisle: { type: 'number', format: '01-99', length: 2 },
    rack: { type: 'number', format: '001-999', length: 3 },
    shelf: { type: 'number', format: '01-10', length: 2 },
    bin: { type: 'number', format: '01-20', length: 2 },
    separator: '-'
  };

  const normalizePattern = (input: unknown): LocationCodingRule['pattern'] => {
    try {
      let obj: any = input;
      if (typeof obj === 'string') {
        obj = JSON.parse(obj);
      }
      if (obj && typeof obj === 'object') {
        return {
          zone: obj.zone || defaultPattern.zone,
          aisle: obj.aisle || defaultPattern.aisle,
          rack: obj.rack || defaultPattern.rack,
          shelf: obj.shelf || defaultPattern.shelf,
          bin: obj.bin || defaultPattern.bin,
          separator: obj.separator || defaultPattern.separator
        } as LocationCodingRule['pattern'];
      }
    } catch {}
    return defaultPattern;
  };

  const normalizeExamples = (input: unknown): string[] => {
    try {
      if (Array.isArray(input)) {
        return input.map(String);
      }
      if (typeof input === 'string') {
        const parsed = JSON.parse(input);
        return Array.isArray(parsed) ? parsed.map(String) : [];
      }
    } catch {}
    return [];
  };
  
  // 倉庫概覽排序和列設置狀態
  const [warehouseSortField, setWarehouseSortField] = useState<string>('');
  const [warehouseSortOrder, setWarehouseSortOrder] = useState<'asc' | 'desc' | ''>('');
  const [warehouseColumnSettings, setWarehouseColumnSettings] = useState(() => {
    const saved = localStorage.getItem('new-warehouse-columns-config');
    const version = localStorage.getItem('new-warehouse-columns-version');

    const makeDefault = () => ({
      name: { visible: true, order: 0 },
      code: { visible: true, order: 1 },
      type: { visible: true, order: 2 },
      capacity: { visible: true, order: 3 },
      efficiency: { visible: true, order: 4 },
      accuracy: { visible: true, order: 5 },
      throughput: { visible: true, order: 6 },
      status: { visible: true, order: 7 },
      actions: { visible: true, order: 8 },
    });

    const VERSION = '2025-02-v1';

    // 初次或版本不同：使用新欄位默認配置
    if (version !== VERSION || !saved) {
      const defaultConfig = makeDefault();
      localStorage.setItem('new-warehouse-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('new-warehouse-columns-version', VERSION);
      return defaultConfig;
    }

    // 嘗試讀取現有配置，並在檢測到舊鍵(info/performance)時進行遷移
    try {
      const old = JSON.parse(saved);
      const hasOldKeys = !!(old.info || old.performance);
      const missingNewKeys = !('name' in old) || !('efficiency' in old) || !('accuracy' in old) || !('throughput' in old);

      if (hasOldKeys || missingNewKeys) {
        const migrated = {
          name: { visible: old.info?.visible ?? true, order: 0 },
          code: { visible: old.info?.visible ?? true, order: 1 },
          type: { visible: old.info?.visible ?? true, order: 2 },
          capacity: { visible: old.capacity?.visible ?? true, order: 3 },
          efficiency: { visible: old.performance?.visible ?? true, order: 4 },
          accuracy: { visible: old.performance?.visible ?? true, order: 5 },
          throughput: { visible: old.performance?.visible ?? true, order: 6 },
          status: { visible: old.status?.visible ?? true, order: 7 },
          actions: { visible: old.actions?.visible ?? true, order: 8 },
        };
        localStorage.setItem('new-warehouse-columns-config', JSON.stringify(migrated));
        localStorage.setItem('new-warehouse-columns-version', VERSION);
        return migrated;
      }

      // 補齊缺失鍵並移除未知鍵
      const base = makeDefault();
      const normalized: any = {};
      Object.keys(base).forEach((k) => {
        normalized[k] = old[k] ?? base[k];
      });
      localStorage.setItem('new-warehouse-columns-config', JSON.stringify(normalized));
      localStorage.setItem('new-warehouse-columns-version', VERSION);
      return normalized;
    } catch {
      const fallback = makeDefault();
      localStorage.setItem('new-warehouse-columns-config', JSON.stringify(fallback));
      localStorage.setItem('new-warehouse-columns-version', VERSION);
      return fallback;
    }
  });

  // 編碼管理排序和列設置狀態
  const [codingSortField, setCodingSortField] = useState<string>('');
  const [codingSortOrder, setCodingSortOrder] = useState<'asc' | 'desc' | ''>('');
  const [codingColumnSettings, setCodingColumnSettings] = useState(() => {
    const saved = localStorage.getItem('new-coding-columns-config');
    const version = localStorage.getItem('new-coding-columns-version');
    
    if (version !== '2024-01-v3' || !saved) {
      const defaultConfig = {
        name: { visible: true, order: 0 },
        pattern: { visible: true, order: 1 },
        capacity: { visible: true, order: 2 },
        examples: { visible: true, order: 3 },
        temperature: { visible: true, order: 4 },
        actions: { visible: true, order: 5 }
      };
      localStorage.setItem('new-coding-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('new-coding-columns-version', '2024-01-v3');
      return defaultConfig;
    }
    
    try {
      return JSON.parse(saved);
    } catch {
      return {
        name: { visible: true, order: 0 },
        pattern: { visible: true, order: 1 },
        capacity: { visible: true, order: 2 },
        examples: { visible: true, order: 3 },
        temperature: { visible: true, order: 4 },
        actions: { visible: true, order: 5 }
      };
    }
  });

  // 列設置下拉菜單可見狀態
  const [warehouseColumnSettingsVisible, setWarehouseColumnSettingsVisible] = useState(false);
  const [codingColumnSettingsVisible, setCodingColumnSettingsVisible] = useState(false);
  
  // 模態框狀態
  const [warehouseModalVisible, setWarehouseModalVisible] = useState(false);
  const [locationModalVisible, setLocationModalVisible] = useState(false);
  const [codingRuleModalVisible, setCodingRuleModalVisible] = useState(false);
  // 移除智能向導

  
  // 表單
  const [warehouseForm] = Form.useForm();
  const [locationForm] = Form.useForm();
  const [codingForm] = Form.useForm();
  // 移除智能向導表單
  
  // 編輯狀態
  const [editingWarehouse, setEditingWarehouse] = useState<UnifiedWarehouse | null>(null);
  const [editingLocation, setEditingLocation] = useState<WarehouseLocation | null>(null);
  const [editingRule, setEditingRule] = useState<LocationCodingRule | null>(null);
  
  // 智能向導狀態
  // 移除智能向導狀態

  // 當打開編輯彈窗時，回顯當前倉庫數據（避免偶發未回顯）
  useEffect(() => {
    if (warehouseModalVisible && editingWarehouse) {
      warehouseForm.setFieldsValue({
        ...editingWarehouse,
        capacityTotal: editingWarehouse.capacity?.total,
        capacityUnit: editingWarehouse.capacity?.unit,
        capacityUsed: editingWarehouse.capacity?.used,
        capacityReserved: editingWarehouse.capacity?.reserved,
        temperatureRequirement: editingWarehouse.temperatureRequirement,
      });
    }
  }, [warehouseModalVisible, editingWarehouse]);
  
  // 搜索和過濾
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState<'ALL' | WarehouseLocation['type']>('ALL');

  // 展開收起控制
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const toggleCollapsed = () => setCollapsed(prev => !prev);

  // 切換倉庫時，清除已生成的位置注入，避免跨倉庫污染
  useEffect(() => {
    setGeneratedLocations(null);
    setApplyToken(t => t + 1);
  }, [selectedWarehouse?.id]);

  // 位置類型配置
  const locationTypeConfig = {
    ZONE: { label: t('區域'), icon: <ApartmentOutlined />, color: 'blue', level: 1 },
    AISLE: { label: t('通道'), icon: <BarsOutlined />, color: 'green', level: 2 },
    RACK: { label: t('貨架'), icon: <DatabaseOutlined />, color: 'orange', level: 3 },
    SHELF: { label: t('層'), icon: <BoxPlotOutlined />, color: 'purple', level: 4 },
    BIN: { label: t('儲位'), icon: <HomeOutlined />, color: 'red', level: 5 },
    PACKING_ZONE: { label: t('包裝區'), icon: <BoxPlotOutlined />, color: 'magenta', level: 1 },
  };

  // 倉庫排序處理
  const handleWarehouseColumnSort = (columnKey: string) => {
    if (warehouseSortField === columnKey) {
      setWarehouseSortOrder(warehouseSortOrder === 'asc' ? 'desc' : warehouseSortOrder === 'desc' ? '' : 'asc');
      if (warehouseSortOrder === 'desc') {
        setWarehouseSortField('');
      }
    } else {
      setWarehouseSortField(columnKey);
      setWarehouseSortOrder('asc');
    }
  };

  const getWarehouseSortIcon = (columnKey: string) => {
    if (warehouseSortField !== columnKey) return null;
    return warehouseSortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  const handleWarehouseColumnVisibilityChange = (columnKey: string, visible: boolean) => {
    const newSettings = {
      ...warehouseColumnSettings,
      [columnKey]: { ...warehouseColumnSettings[columnKey], visible }
    };
    setWarehouseColumnSettings(newSettings);
    localStorage.setItem('new-warehouse-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('new-warehouse-columns-version', '2024-01-v2');
  };

  // 編碼管理排序處理
  const handleCodingColumnSort = (columnKey: string) => {
    if (codingSortField === columnKey) {
      setCodingSortOrder(codingSortOrder === 'asc' ? 'desc' : codingSortOrder === 'desc' ? '' : 'asc');
      if (codingSortOrder === 'desc') {
        setCodingSortField('');
      }
    } else {
      setCodingSortField(columnKey);
      setCodingSortOrder('asc');
    }
  };

  const getCodingSortIcon = (columnKey: string) => {
    if (codingSortField !== columnKey) return null;
    return codingSortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  const handleCodingColumnVisibilityChange = (columnKey: string, visible: boolean) => {
    const newSettings = {
      ...codingColumnSettings,
      [columnKey]: { ...codingColumnSettings[columnKey], visible }
    };
    setCodingColumnSettings(newSettings);
    localStorage.setItem('new-coding-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('new-coding-columns-version', '2024-01-v3');
  };

  // 初始化數據：改為調用後端 API
  useEffect(() => {
    loadWarehouses();
  }, [warehousePage, warehousePageSize]);

  const mapRowToUnified = (r: any): UnifiedWarehouse => ({
    id: r.id,
    name: r.name,
    code: r.code,
    type: r.type,
    location: {
      address: r.addressStreet || '',
      coordinates: { lat: 0, lng: 0 },
      timezone: 'Asia/Shanghai',
      country: r.addressCountry || 'CN',
    },
    capacity: {
      total: Number(r.capacityTotal) || 0,
      used: Number(r.capacityUsed) || 0,
      reserved: Number(r.capacityReserved) || 0,
      available: Number(r.capacityAvailable) || 0,
      unit: r.capacityUnit || 'cbm',
    },
    zones: [],
    locationRules: [],
    technology: { wms: true, rfid: true, automation: true, iot_sensors: true, robotics: true },
    performance: {
      efficiency: Number(r.efficiency) || 0,
      accuracy: Number(r.accuracy) || 0,
      throughput: Number(r.throughput) || 0,
      costs: Number(r.costs) || 0,
    },
    status: (r.isActive ? 'operational' : 'maintenance') as any,
    created_at: r.createdAt,
    updated_at: r.updatedAt,
    temperatureRequirement: r.temperatureRequirement,
  });

  const loadWarehouses = async () => {
    try {
      const resp: any = await apiService.getWarehouses({ page: warehousePage, limit: warehousePageSize });
      const rows: any[] = resp.data || [];
      const mapped: UnifiedWarehouse[] = rows.map(mapRowToUnified);
      setWarehouses(mapped);
      setWarehouseTotal(resp?.pagination?.total ?? mapped.length);
      if (!selectedWarehouse && mapped.length > 0) {
        setSelectedWarehouse(mapped[0]);
      }
    } catch (e) {
      console.error('load warehouses error:', e);
    }
  };

  const loadMockData = () => {
    const mockWarehouses: UnifiedWarehouse[] = [
      {
        id: 'wh-unified-001',
        name: '智能物流中心 Alpha',
        code: 'SMART-01',
        type: 'smart',
        location: {
          address: '香港葵青區葵昌路100號',
          coordinates: { lat: 22.3193, lng: 114.1694 },
          timezone: 'Asia/Hong_Kong',
          country: 'HK',
        },
        capacity: {
          total: 15000,
          used: 9800,
          reserved: 2200,
          available: 3000,
          unit: 'cbm',
        },
        zones: [],
        locationRules: [],
        technology: {
          wms: true,
          rfid: true,
          automation: true,
          iot_sensors: true,
          robotics: true,
        },
        performance: {
          efficiency: 94,
          accuracy: 99.8,
          throughput: 2500,
          costs: 3.2,
        },
        status: 'operational',
        created_at: '2023-06-15',
        updated_at: '2024-01-20',
        temperatureRequirement: 'room_temperature',
      },
      {
        id: 'wh-unified-002',
        name: '自動化配送中心 Beta',
        code: 'AUTO-02',
        type: 'automated',
        location: {
          address: '深圳市寶安區福永街道福圍社區101號',
          coordinates: { lat: 22.7542, lng: 113.8144 },
          timezone: 'Asia/Shanghai',
          country: 'CN',
        },
        capacity: {
          total: 25000,
          used: 18500,
          reserved: 3500,
          available: 3000,
          unit: 'cbm',
        },
        zones: [],
        locationRules: [],
        technology: {
          wms: true,
          rfid: true,
          automation: true,
          iot_sensors: true,
          robotics: true,
        },
        performance: {
          efficiency: 89,
          accuracy: 98.5,
          throughput: 3200,
          costs: 2.8,
        },
        status: 'operational',
        created_at: '2023-08-10',
        updated_at: '2024-01-18',
        temperatureRequirement: 'cool',
      },
      {
        id: 'wh-unified-003',
        name: '傳統倉儲中心 Gamma',
        code: 'TRAD-03',
        type: 'traditional',
        location: {
          address: '台北市中山區南京東路二段123號',
          coordinates: { lat: 25.0520, lng: 121.5437 },
          timezone: 'Asia/Taipei',
          country: 'TW',
        },
        capacity: {
          total: 8000,
          used: 5200,
          reserved: 1200,
          available: 1600,
          unit: 'cbm',
        },
        zones: [],
        locationRules: [],
        technology: {
          wms: false,
          rfid: false,
          automation: false,
          iot_sensors: true,
          robotics: false,
        },
        performance: {
          efficiency: 75,
          accuracy: 95.2,
          throughput: 1200,
          costs: 4.1,
        },
        status: 'operational',
        created_at: '2022-12-05',
        updated_at: '2024-01-10',
        temperatureRequirement: 'cold',
      },
      {
        id: 'wh-unified-004',
        name: '冷藏物流中心 Delta',
        code: 'COLD-04',
        type: 'cold_storage',
        location: {
          address: '上海市浦東新區張江高科技園區碧波路456號',
          coordinates: { lat: 31.2065, lng: 121.6016 },
          timezone: 'Asia/Shanghai',
          country: 'CN',
        },
        capacity: {
          total: 12000,
          used: 7800,
          reserved: 2400,
          available: 1800,
          unit: 'cbm',
        },
        zones: [],
        locationRules: [],
        technology: {
          wms: true,
          rfid: true,
          automation: false,
          iot_sensors: true,
          robotics: false,
        },
        performance: {
          efficiency: 82,
          accuracy: 97.8,
          throughput: 1800,
          costs: 5.5,
        },
        status: 'operational',
        created_at: '2023-03-20',
        updated_at: '2024-01-12',
      },
      {
        id: 'wh-unified-005',
        name: '危化品倉庫 Epsilon',
        code: 'HAZ-05',
        type: 'hazmat',
        location: {
          address: '廣州市南沙區東涌鎮環市大道78號',
          coordinates: { lat: 22.9328, lng: 113.3713 },
          timezone: 'Asia/Shanghai',
          country: 'CN',
        },
        capacity: {
          total: 5000,
          used: 2100,
          reserved: 800,
          available: 2100,
          unit: 'cbm',
        },
        zones: [],
        locationRules: [],
        technology: {
          wms: true,
          rfid: true,
          automation: false,
          iot_sensors: true,
          robotics: false,
        },
        performance: {
          efficiency: 68,
          accuracy: 99.5,
          throughput: 450,
          costs: 8.2,
        },
        status: 'maintenance',
        created_at: '2023-09-15',
        updated_at: '2024-01-10',
      },
    ];

    const mockCodingRules: LocationCodingRule[] = [
      {
        id: 'rule-001',
        name: '標準編碼規則',
        description: t('適用於一般倉庫的標準儲位編碼'),
        warehouseId: 'wh-unified-001',
        isDefault: true,
        isActive: true,
        pattern: {
          zone: { type: 'letter', format: 'A-H' },
          aisle: { type: 'number', format: '01-99', length: 2 },
          rack: { type: 'number', format: '001-999', length: 3 },
          shelf: { type: 'number', format: '01-10', length: 2 },
          bin: { type: 'number', format: '01-20', length: 2 },
          separator: '-',
        },
        examples: ['A-01-001-01-01', 'B-15-025-05-12', 'C-08-150-03-08'],
        totalPositions: 1584000,
        estimatedCapacity: 3168000,
        created_at: '2024-01-15',
        updated_at: '2024-01-20',
      },
      {
        id: 'rule-002',
        name: '語義化編碼規則',
        description: t('使用有意義的縮寫，便於理解和記憶'),
        warehouseId: 'wh-unified-002',
        isDefault: false,
        isActive: true,
        pattern: {
          zone: { type: 'custom', format: 'CUSTOM_LIST', customList: ['REC', 'STO', 'PCK', 'SHP'] },
          aisle: { type: 'letter', format: 'A-Z' },
          rack: { type: 'number', format: '001-999', length: 3 },
          shelf: { type: 'number', format: '01-20', length: 2 },
          bin: { type: 'number', format: '01-50', length: 2 },
          separator: '-',
        },
        examples: ['REC-A-001-01-01', 'STO-B-125-08-15', 'PCK-C-250-12-30'],
        totalPositions: 2080000,
        estimatedCapacity: 4160000,
        created_at: '2024-01-10',
        updated_at: '2024-01-18',
      },
      {
        id: 'rule-003',
        name: '高密度緊湊編碼',
        description: t('最大化儲位數量，適合高密度存儲'),
        warehouseId: 'wh-unified-003',
        isDefault: false,
        isActive: true,
        pattern: {
          zone: { type: 'number', format: '1-9' },
          aisle: { type: 'number', format: '01-99', length: 2 },
          rack: { type: 'number', format: '001-999', length: 3 },
          shelf: { type: 'number', format: '01-30', length: 2 },
          bin: { type: 'number', format: '01-99', length: 2 },
          separator: '',
        },
        examples: ['10100101', '25123515', '98765430'],
        totalPositions: 26673300,
        estimatedCapacity: 53346600,
        created_at: '2023-12-20',
        updated_at: '2024-01-15',
      },
      {
        id: 'rule-004',
        name: '冷鏈專用編碼',
        description: t('針對冷藏倉庫設計的編碼規則'),
        warehouseId: 'wh-unified-004',
        isDefault: true,
        isActive: true,
        pattern: {
          zone: { type: 'custom', format: 'CUSTOM_LIST', customList: ['FREEZE', 'CHILL', 'DRY'] },
          aisle: { type: 'letter', format: 'A-P' },
          rack: { type: 'number', format: '01-50', length: 2 },
          shelf: { type: 'number', format: '01-08', length: 2 },
          bin: { type: 'number', format: '01-12', length: 2 },
          separator: '.',
        },
        examples: ['FREEZE.A.01.01.01', 'CHILL.B.25.05.08', 'DRY.P.50.08.12'],
        totalPositions: 230400,
        estimatedCapacity: 460800,
        temperature: {
          zone: { min: -25, max: -15, unit: 'celsius', isControlled: true },
          aisle: { min: -23, max: -17, unit: 'celsius', isControlled: true },
          rack: { min: -22, max: -18, unit: 'celsius', isControlled: true },
          shelf: { min: -21, max: -19, unit: 'celsius', isControlled: true },
          bin: { min: -20, max: -20, unit: 'celsius', isControlled: true },
        },
        created_at: '2023-11-15',
        updated_at: '2024-01-12',
      },
      {
        id: 'rule-005',
        name: '危化品安全編碼',
        description: t('符合安全規範的危化品倉庫編碼'),
        warehouseId: 'wh-unified-005',
        isDefault: true,
        isActive: true,
        pattern: {
          zone: { type: 'custom', format: 'CUSTOM_LIST', customList: ['FLAM', 'CORR', 'TOXI', 'EXPL'] },
          aisle: { type: 'number', format: '01-10', length: 2 },
          rack: { type: 'number', format: '01-20', length: 2 },
          shelf: { type: 'number', format: '01-05', length: 2 },
          bin: { type: 'number', format: '01-08', length: 2 },
          separator: '_',
        },
        examples: ['FLAM_01_01_01_01', 'CORR_05_15_03_05', 'TOXI_10_20_05_08'],
        totalPositions: 32000,
        estimatedCapacity: 64000,
        temperature: {
          zone: { min: 15, max: 25, unit: 'celsius', isControlled: true },
          aisle: { min: 17, max: 23, unit: 'celsius', isControlled: true },
          rack: { min: 18, max: 22, unit: 'celsius', isControlled: true },
          shelf: { min: 19, max: 21, unit: 'celsius', isControlled: true },
          bin: { min: 20, max: 20, unit: 'celsius', isControlled: true },
        },
        created_at: '2023-10-01',
        updated_at: '2024-01-10',
      },
    ];

    setWarehouses(mockWarehouses);
    setSelectedWarehouse(mockWarehouses[0]);
    setCodingRules(mockCodingRules);
    // 設置分頁 total（未接後端時用前端數量）
    setWarehouseTotal(mockWarehouses.length);
    setCodingTotal(mockCodingRules.length);
  };

  // 導出倉庫Excel功能 - 客戶端CSV生成
  const handleExportWarehousesExcel = () => {
    try {
      // 生成CSV內容
      const csvContent = generateWarehousesCSVContent(sortedWarehouses);
      
      // 創建Blob並下載
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `warehouses_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success(t('倉庫數據已成功導出'));
    } catch (error) {
      message.error(t('導出失敗，請稍後重試'));
    }
  };

  // 導出編碼規則Excel功能 - 客戶端CSV生成
  const handleExportCodingRulesExcel = () => {
    try {
      // 生成CSV內容
      const csvContent = generateCodingRulesCSVContent(codingRules);
      
      // 創建Blob並下載
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `coding_rules_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success(t('編碼規則數據已成功導出'));
    } catch (error) {
      console.error('導出失敗:', error);
      message.error(t('導出失敗，請稍後重試'));
    }
  };

  // 生成倉庫CSV內容
  const generateWarehousesCSVContent = (data: UnifiedWarehouse[]) => {
    const headers = [
      '倉庫ID', '倉庫名稱', '倉庫代碼', '類型', '地址', '總容量', '已使用容量', '預留容量', 
      '可用容量', '容量單位', '技術配置', '狀態', '創建時間', '更新時間'
    ];

    const rows = data.map(item => [
      item.id || '',
      item.name || '',
      item.code || '',
      item.type || '',
      item.location?.address || '',
      item.capacity?.total || '',
      item.capacity?.used || '',
      item.capacity?.reserved || '',
      item.capacity?.available || '',
      item.capacity?.unit || '',
      item.technology ? Object.entries(item.technology).filter(([_, v]) => v).map(([k, _]) => k).join('; ') : '',
      item.status || '',
      item.created_at ? `\u200B${new Date(item.created_at).toISOString().split('T')[0]}` : '',
      item.updated_at ? `\u200B${new Date(item.updated_at).toISOString().split('T')[0]}` : ''
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  // 生成編碼規則CSV內容
  const generateCodingRulesCSVContent = (data: CodingRule[]) => {
    const headers = [
      '規則ID', '規則名稱', '描述', '倉庫ID', '是否默認', '是否啟用', '分隔符',
      '區域格式', '通道格式', '貨架格式', '層格式', '位格式', '示例', '創建時間', '更新時間'
    ];

    const rows = data.map(item => [
      item.id || '',
      item.name || '',
      item.description || '',
      item.warehouseId || '',
      item.isDefault ? '是' : '否',
      item.isActive ? '是' : '否',
      item.pattern?.separator || '',
      item.pattern?.zone ? JSON.stringify(item.pattern.zone) : '',
      item.pattern?.aisle ? JSON.stringify(item.pattern.aisle) : '',
      item.pattern?.rack ? JSON.stringify(item.pattern.rack) : '',
      item.pattern?.shelf ? JSON.stringify(item.pattern.shelf) : '',
      item.pattern?.bin ? JSON.stringify(item.pattern.bin) : '',
      item.examples ? item.examples.join('; ') : '',
      item.created_at || '',
      item.updated_at || ''
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  // 排序後的倉庫數據
  const sortedWarehouses = useMemo(() => {
    let result = [...warehouses];
    
    if (warehouseSortField && warehouseSortOrder) {
      result.sort((a, b) => {
        let aValue: any = a[warehouseSortField as keyof UnifiedWarehouse];
        let bValue: any = b[warehouseSortField as keyof UnifiedWarehouse];
        
        // 處理嵌套字段排序
        if (warehouseSortField === 'capacity.used') {
          aValue = a.capacity?.used || 0;
          bValue = b.capacity?.used || 0;
        } else if (warehouseSortField === 'performance.efficiency') {
          aValue = a.performance?.efficiency || 0;
          bValue = b.performance?.efficiency || 0;
        }
        
        // 處理日期字段排序
        if (['created_at', 'updated_at'].includes(warehouseSortField)) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        
        // 處理字符串字段排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return warehouseSortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return warehouseSortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [warehouses, warehouseSortField, warehouseSortOrder]);

  // 從後端拉取編碼規則（移除純前端排序為主資料來源）

  useEffect(() => {
    loadCodingRules();
  }, [codingPage, codingPageSize, codingSortField, codingSortOrder]);

  const loadCodingRules = async () => {
    try {
      const resp: any = await apiService.getCodingRules({
        page: codingPage,
        limit: codingPageSize,
        sortField: codingSortField,
        sortOrder: codingSortOrder || 'desc'
      });
      const rows: any[] = resp.data || [];
      const mapped: LocationCodingRule[] = rows.map((r: any) => {
        const pat = normalizePattern(r && r.pattern !== undefined ? r.pattern : undefined);
        const exs = normalizeExamples(r && r.examples !== undefined ? r.examples : undefined);
        const rule: LocationCodingRule = {
          id: String(r.id ?? ''),
          name: String(r.name ?? ''),
          description: String(r.description ?? ''),
          warehouseId: String(r.warehouseId ?? ''),
          isDefault: !!r.isDefault,
          isActive: !!r.isActive,
          pattern: pat as any,
          examples: exs,
          totalPositions: toInt(r.totalPositions),
          estimatedCapacity: toInt(r.estimatedCapacity),
          created_at: r && r.createdAt ? String(r.createdAt) : '',
          updated_at: r && r.updatedAt ? String(r.updatedAt) : ''
        };
        return rule;
      });
      setCodingRules(mapped);
      const totalFromResp = resp && resp.pagination && typeof resp.pagination.total === 'number' ? resp.pagination.total : undefined;
      setCodingTotal(totalFromResp !== undefined ? totalFromResp : mapped.length);
    } catch (e) {
      console.error('load coding rules error:', e);
    }
  };

  // 生成位置編碼預覽
  const generateLocationPreview = (rule: LocationCodingRule, count: number = 20): string[] => {
    const codes: string[] = [];
    const { pattern } = rule;

    const aisleLen = Math.max(1, Number(pattern?.aisle?.length ?? 2));
    const rackLen = Math.max(1, Number(pattern?.rack?.length ?? 3));
    const shelfLen = Math.max(1, Number(pattern?.shelf?.length ?? 2));
    const binLen = Math.max(1, Number(pattern?.bin?.length ?? 2));
    const separator = typeof pattern?.separator === 'string' ? pattern.separator : '-';

    for (let i = 0; i < count; i++) {
      let code = '';

      // Zone
      if (pattern?.zone?.type === 'letter') {
        const start = String(pattern.zone.format || 'A-Z').split('-')[0] || 'A';
        const charCode = start.charCodeAt(0) + (i % 8);
        code += String.fromCharCode(charCode);
      } else if (pattern?.zone?.type === 'custom' && Array.isArray(pattern.zone.customList) && pattern.zone.customList.length > 0) {
        code += pattern.zone.customList[i % pattern.zone.customList.length];
      } else {
        code += String((i % 9) + 1);
      }

      code += separator;

      // Aisle
      const aisleNum = String((i % 20) + 1).padStart(aisleLen, '0');
      code += aisleNum + separator;

      // Rack
      const rackNum = String((i % 50) + 1).padStart(rackLen, '0');
      code += rackNum + separator;

      // Shelf
      const shelfNum = String((i % 10) + 1).padStart(shelfLen, '0');
      code += shelfNum + separator;

      // Bin
      const binNum = String((i % 20) + 1).padStart(binLen, '0');
      code += binNum;

      codes.push(code);
    }

    return codes;
  };

  // 編碼向導步驟
  // 移除智能向導步驟



  // 設定預設倉庫
  const handleSetDefaultWarehouse = async (warehouseId: string, warehouseName: string) => {
    try {
      await apiService.setDefaultWarehouse(warehouseId);
      message.success(t('已將「{{warehouseName}}」設為預設倉庫', { warehouseName }));
      await loadWarehouses();
    } catch (err: any) {
      message.error(err?.message || t('設定預設倉庫失敗'));
    }
  };

  // 定義所有可用的倉庫列
  const allWarehouseColumns = {
    name: {
      title: t('倉庫名稱'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name: string, record: UnifiedWarehouse) => (
        <Space>
          <Text>{name}</Text>
          {record.isDefault && <Tag color="gold"><StarOutlined /> {t('預設')}</Tag>}
        </Space>
      ),
    },
    code: { title: t('代碼'), dataIndex: 'code', key: 'code', width: 120, sorter: true },
    type: { title: t('類型'), dataIndex: 'type', key: 'type', width: 140 },
    capacity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWarehouseColumnSort('capacity.used')}
        >
          {t('容量使用')}
                          {getWarehouseSortIcon('capacity.used')}
        </div>
      ),
      key: 'capacity',
      sorter: true,
      render: (_, record) => {
        const rate = (record.capacity.used / record.capacity.total) * 100;
        return (
          <div>
            <Progress percent={Math.round(rate)} size="small" />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.capacity.used} / {record.capacity.total} {record.capacity.unit}
            </Text>
          </div>
        );
      },
    },
    efficiency: { title: t('效率(%)'), key: 'efficiency', sorter: true, render: (_, r) => <Text>{r.performance.efficiency}</Text>, width: 100 },
    accuracy: { title: t('準確率(%)'), key: 'accuracy', render: (_, r) => <Text>{r.performance.accuracy}</Text>, width: 110 },
    throughput: { title: t('吞吐量'), key: 'throughput', render: (_, r) => <Text>{r.performance.throughput}</Text>, width: 120 },
    status: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleWarehouseColumnSort('status')}
        >
          {t('狀態')}
                          {getWarehouseSortIcon('status')}
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status) => {
        const configs = {
          operational: { color: 'green', text: t('運營中') },
          maintenance: { color: 'orange', text: t('維護中') },
          construction: { color: 'blue', text: t('建設中') },
          offline: { color: 'red', text: t('離線') },
        };
        return <Badge status={configs[status].color as any} text={configs[status].text} />;
      },
    },
    actions: {
      title: t('操作'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedWarehouse(record);
              // 回填只讀表單
              warehouseForm.setFieldsValue({
                ...record,
                capacityTotal: record.capacity?.total,
                capacityUnit: record.capacity?.unit,
                capacityUsed: record.capacity?.used,
                capacityReserved: record.capacity?.reserved,
                temperatureRequirement: record.temperatureRequirement,
              });
              setViewModalVisible(true);
            }}
          >
            {t('詳情')}
                            </Button>
          <Button
            type="link"
            icon={<EnvironmentOutlined />}
            onClick={() => {
              setSelectedWarehouse(record);
              setActiveTab('locations');
            }}
          >
            {t('管理位置')}
                            </Button>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => {
              setEditingWarehouse(record);
              warehouseForm.setFieldsValue({
                ...record,
                capacityTotal: record.capacity?.total,
                capacityUnit: record.capacity?.unit,
                capacityUsed: record.capacity?.used,
                capacityReserved: record.capacity?.reserved,
                temperatureRequirement: record.temperatureRequirement,
              });
              setWarehouseModalVisible(true);
            }}
          >
            {t('編輯')}
                            </Button>
          <Popconfirm
            title={t('確定要刪除這個倉庫嗎？')}
            onConfirm={async () => {
              try {
                await apiService.deleteWarehouse(record.id);
                message.success(t('倉庫刪除成功'));
                // 重新載入列表
                await loadWarehouses();
              } catch (err: any) {
                const msg = err?.message || t('刪除失敗');
                message.error(msg);
              }
            }}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              {t('刪除')}
                                  </Button>
          </Popconfirm>
        </Space>
      ),
    },
  };

  // 定義所有可用的編碼規則列
  const allCodingRuleColumns = {
    name: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleCodingColumnSort('name')}
        >
          {t('規則名稱')}
                          {getCodingSortIcon('name')}
        </div>
      ),
      key: 'name',
      sorter: true,
      render: (_, record) => (
        <div>
          <Space>
            <Text strong>{record.name}</Text>
            {record.isDefault && <Tag color="gold">{t('預設')}</Tag>}
          </Space>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.description}
          </Text>
        </div>
      ),
    },
    pattern: {
      title: t('編碼格式'),
      key: 'pattern',
      render: (_, record) => {
        const { pattern } = record;
        return (
          <div style={{ fontSize: '12px' }}>
            <div>{t('區域:')} {pattern.zone.format}</div>
            <div>{t('通道:')} {pattern.aisle.format}</div>
            <div>{t('貨架:')} {pattern.rack.format}</div>
            <div>{t('層:')} {pattern.shelf.format}</div>
            <div>{t('儲位:')} {pattern.bin.format}</div>
            <div>{t('分隔符: "')}{pattern.separator}"</div>
          </div>
        );
      },
    },
    capacity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleCodingColumnSort('totalPositions')}
        >
          {t('容量預估')}
                          {getCodingSortIcon('totalPositions')}
        </div>
      ),
      key: 'capacity',
      sorter: true,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Text style={{ fontSize: '12px' }}>{t('總儲位:')} {record.totalPositions.toLocaleString()}</Text>
          <Text style={{ fontSize: '12px' }}>{t('估計容量:')} {record.estimatedCapacity.toLocaleString()}</Text>
        </Space>
      ),
    },
    examples: {
      title: t('示例'),
      dataIndex: 'examples',
      key: 'examples',
      render: (examples) => (
        <div>
          {examples.slice(0, 2).map((example, index) => (
            <Tag key={index} style={{ fontSize: '10px', marginBottom: 2 }}>
              {example}
            </Tag>
          ))}
          {examples.length > 2 && <Text type="secondary">...</Text>}
        </div>
      ),
    },
    temperature: {
      title: t('溫度控制'),
      key: 'temperature',
      render: (_, record) => {
        if (!record.temperature) {
          return <Text type="secondary">{t('未設置')}</Text>;
        }
        
        const renderTempRange = (level: string) => {
          const temp = record.temperature[level as keyof typeof record.temperature];
          if (!temp) return null;
          
          return (
            <div key={level} style={{ fontSize: '11px', marginBottom: 2 }}>
              <Text strong>{level === 'zone' ? t('區域') : level === 'aisle' ? t('通道') : level === 'rack' ? t('貨架') : level === 'shelf' ? t('層') : t('儲位')}:</Text>
              {temp.isControlled ? (
                <Tag color="green" size="small">
                  {temp.min}°C - {temp.max}°C
                </Tag>
              ) : (
                <Tag color="orange" size="small">{t('未控制')}</Tag>
              )}
            </div>
          );
        };
        
        const levels = ['zone', 'aisle', 'rack', 'shelf', 'bin'];
        const controlledLevels = levels.filter(level => 
          record.temperature[level as keyof typeof record.temperature]?.isControlled
        );
        
        return (
          <div>
            {controlledLevels.length > 0 ? (
              levels.map(renderTempRange).filter(Boolean)
            ) : (
              <Text type="secondary">{t('未設置溫度控制')}</Text>
            )}
          </div>
        );
      },
    },
    actions: {
      title: t('操作'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EyeOutlined />}
            onClick={async () => {
              try {
                const resp: any = await apiService.getCodingRulePreview(record.id);
                const preview: string[] = resp?.preview || [];
                Modal.info({
                  title: t('編碼預覽'),
                  width: 600,
                  content: (
                    <div>
                      <div style={{ marginBottom: 16 }}>
                        <Text strong>{t('規則:')} {record.name}</Text>
                      </div>
                      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                        {preview.map((code, index) => (
                          <Tag key={index} style={{ margin: 2, fontFamily: 'monospace' }}>
                            {code}
                          </Tag>
                        ))}
                        {preview.length === 0 && <Text type="secondary">{t('暫無預覽數據')}</Text>}
                      </div>
                    </div>
                  ),
                });
              } catch (err: any) {
                const msg = err?.message || t('獲取預覽失敗');
                message.error(msg);
              }
            }}
          >
            {t('預覽')}
                            </Button>

          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => {
              setEditingRule(record);
              codingForm.setFieldsValue(record);
              setCodingRuleModalVisible(true);
            }}
          >
            {t('編輯')}
                            </Button>
          <Popconfirm
            title={t('確定要刪除這個編碼規則嗎？')}
            onConfirm={async () => {
              try {
                await apiService.deleteCodingRule(record.id);
                message.success(t('編碼規則刪除成功'));
                await loadCodingRules();
              } catch (err: any) {
                const msg = err?.message || t('刪除失敗');
                message.error(msg);
              }
            }}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  };

  // 倉庫列設置菜單
  const warehouseColumnSettingsMenu = (
    <div style={{ 
      background: '#fff', 
      borderRadius: '2px', 
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>{t('倉庫列設置')}</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
        {Object.entries(allWarehouseColumns).map(([key, column]) => (
          <div 
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleWarehouseColumnVisibilityChange(key, !(warehouseColumnSettings[key]?.visible ?? true));
            }}
          >
            <Checkbox
              checked={warehouseColumnSettings[key]?.visible ?? true}
              onChange={(e) => {
                e.stopPropagation();
                handleWarehouseColumnVisibilityChange(key, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {typeof column.title === 'string' ? column.title : 
               key === 'name' ? t('倉庫名稱') :
               key === 'code' ? t('代碼') :
               key === 'type' ? t('類型') :
               key === 'capacity' ? t('容量使用') :
               key === 'efficiency' ? t('效率(%)') :
               key === 'accuracy' ? t('準確率(%)') :
               key === 'throughput' ? t('吞吐量') :
               key === 'status' ? t('狀態') :
               key === 'actions' ? t('操作') : key}
            </Checkbox>
          </div>
        ))}
      </div>
    </div>
  );

  // 編碼列設置菜單
  const codingColumnSettingsMenu = (
    <div style={{ 
      background: '#fff', 
      borderRadius: '2px', 
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>{t('編碼列設置')}</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
        {Object.entries(allCodingRuleColumns).map(([key, column]) => (
          <div 
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleCodingColumnVisibilityChange(key, !(codingColumnSettings[key]?.visible ?? true));
            }}
          >
            <Checkbox
              checked={codingColumnSettings[key]?.visible ?? true}
              onChange={(e) => {
                e.stopPropagation();
                handleCodingColumnVisibilityChange(key, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {typeof column.title === 'string' ? column.title : 
               key === 'name' ? t('規則名稱') :
               key === 'pattern' ? t('編碼格式') :
               key === 'capacity' ? t('容量預估') :
               key === 'examples' ? t('示例') :
               key === 'actions' ? t('操作') : key}
            </Checkbox>
          </div>
        ))}
      </div>
    </div>
  );

  // 生成顯示的倉庫列
  const warehouseColumns = Object.entries(warehouseColumnSettings)
    .filter(([_, config]: [string, any]) => config.visible)
    .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
    .map(([key]) => {
      const column = allWarehouseColumns[key as keyof typeof allWarehouseColumns];
      if (!column) {
        console.warn(`Missing warehouse column definition for key: ${key}`);
      }
      return column;
    })
    .filter(Boolean);

  // 生成顯示的編碼列
  const codingRuleColumns = Object.entries(codingColumnSettings)
    .filter(([_, config]: [string, any]) => config.visible)
    .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
    .map(([key]) => {
      const column = allCodingRuleColumns[key as keyof typeof allCodingRuleColumns];
      if (!column) {
        console.warn(`Missing coding column definition for key: ${key}`);
      }
      return column;
    })
    .filter(Boolean);

  // 渲染倉庫概覽
  const renderWarehouseOverview = () => (
    <div>
      <Alert
        message={t('智能倉庫管理中心')}
        description={t('統一管理倉庫信息和位置編碼')}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <Card
        title={t('倉庫列表')}
        extra={
          <Space>
            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingWarehouse(null);
                  warehouseForm.resetFields();
                  setWarehouseModalVisible(true);
                }}
            >
              {t('新增倉庫')}
                                </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={() => {
                loadWarehouses();
                message.success(t('數據已刷新'));
              }}
            >
              {t('刷新')}
                                </Button>
            <Button
              type="default"
              icon={<FileExcelOutlined />}
              onClick={handleExportWarehousesExcel}
            >
              {t('導出')}
                                </Button>
            <Dropdown 
              overlay={warehouseColumnSettingsMenu} 
              trigger={['click']}
              open={warehouseColumnSettingsVisible}
              onOpenChange={setWarehouseColumnSettingsVisible}
            >
              <Button icon={<SettingOutlined />}>
                {t('列設置')}
                                      </Button>
            </Dropdown>
          </Space>
        }
      >
        <Table
          columns={warehouseColumns}
          dataSource={sortedWarehouses}
          rowKey="id"
          pagination={{ 
            current: warehousePage,
            pageSize: warehousePageSize,
            total: warehouseTotal,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) => t('顯示 {{from}}-{{to}} 項，共 {{total}} 項記錄', { from: range[0], to: range[1], total }),
            onChange: (p, ps) => {
              const newSize = ps || warehousePageSize;
              const newPage = p || 1;
              if (newSize !== warehousePageSize) {
                setWarehousePageSize(newSize);
                setWarehousePage(1);
              } else {
                setWarehousePage(newPage);
              }
            }
          }}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );

  // 渲染編碼規則
  const renderSmartCoding = () => (
    <div>
      <Alert
        message={t('儲位編碼管理')}
        description={t('使用編碼規則，提升倉庫運營效率')}
        type="success"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <Row gutter={16}>
        <Col span={24}>
          <Card
            title={
              <Space>
                <RobotOutlined style={{ color: '#1890ff' }} />
                {t('編碼規則管理')}
                                              <Badge count={codingRules.length} />
              </Space>
            }
            extra={
              <Space>
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    loadCodingRules();
                    message.success(t('數據已刷新'));
                  }}
                >
                  {t('刷新')}
                                        </Button>
                <Button
                  type="default"
                  icon={<FileExcelOutlined />}
                  onClick={handleExportCodingRulesExcel}
                >
                  {t('導出')}
                                        </Button>
                <Dropdown 
                  overlay={codingColumnSettingsMenu} 
                  trigger={['click']}
                  open={codingColumnSettingsVisible}
                  onOpenChange={setCodingColumnSettingsVisible}
                >
                  <Button icon={<SettingOutlined />}>
                    {t('列設置')}
                                              </Button>
                </Dropdown>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingRule(null);
                    codingForm.resetFields();
                    setCodingRuleModalVisible(true);
                  }}
                >
                  {t('新增規則')}
                                        </Button>
              </Space>
            }
          >
            <Table
              columns={codingRuleColumns}
              dataSource={codingRules}
              rowKey="id"
              pagination={{ 
                current: codingPage,
                pageSize: codingPageSize,
                total: codingTotal,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: [10, 20, 50, 100, 500],
                showTotal: (total, range) => t('顯示 {{from}}-{{to}} 項，共 {{total}} 項記錄', { from: range[0], to: range[1], total }),
                onChange: (p, ps) => {
                  const newSize = ps || codingPageSize;
                  const newPage = p || 1;
                  if (newSize !== codingPageSize) {
                    setCodingPageSize(newSize);
                    setCodingPage(1);
                  } else {
                    setCodingPage(newPage);
                  }
                }
              }}
              scroll={{ x: 1200 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );

  // 渲染智能向導內容（已移除）
  const renderWizardContent = () => {
    return null;
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* 標題和展開收起按鈕 */}
      <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>{t('倉庫管理系統')}</Title>
          <Text type="secondary">{t('整合倉庫管理和位置編碼的一站式管理平台')}</Text>
        </div>
        <Button
          type="text"
          icon={collapsed ? <DownOutlined /> : <UpOutlined />}
          onClick={toggleCollapsed}
          style={{ fontSize: 16 }}
        />
      </div>

      {/* KPI 儀表板 */}
      {!collapsed && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Card hoverable>
              <Statistic
                title={t('總倉庫數')}
                value={warehouses.length}
                prefix={<DatabaseOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable>
              <Statistic
                title={t('活躍倉庫')}
                value={warehouses.filter(w => w.status === 'operational').length}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable>
              <Statistic
                title={t('總儲位數')}
                value={warehouses.reduce((sum, w) => sum + (w.capacity.total || 0), 0)}
                prefix={<BoxPlotOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable>
              <Statistic
                title={t('系統狀態')}
                value="正常"
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
        </Row>
      )}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={
            <span>
              <GlobalOutlined />
              {t('倉庫概覽')}
                                      </span>
          } key="overview">
            {renderWarehouseOverview()}
          </TabPane>

          <TabPane tab={
            <span>
              <DatabaseOutlined />
              {t('位置管理')}
                                      </span>
          } key="locations">
            {selectedWarehouse ? (
              <div>
                <Alert
                  message={`當前倉庫: ${selectedWarehouse.name}`}
                  description={t('管理倉庫內的所有位置和區域結構')}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <LocationManagement 
                  warehouse={selectedWarehouse}
                  externalLocations={generatedLocations || undefined}
                  applyToken={applyToken}
                  onLocationUpdate={(locations) => {
                    // 更新倉庫的位置數據
                    console.log('位置數據更新:', locations);
                  }}
                />
              </div>
            ) : (
              <Empty description={t('請先選擇一個倉庫')} />
            )}
          </TabPane>

          <TabPane tab={
            <span>
              <SettingOutlined />
              {t('編碼管理')}
                                      </span>
          } key="coding">
            {renderSmartCoding()}
          </TabPane>

          {/*<TabPane tab={
                <span>
                  <BarChartOutlined />
                  分析報表
                </span>
              } key="analytics">
                <Alert
                  message="智能分析中心"
                  description="倉庫運營數據分析和報表"
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <AnalyticsDashboard
                  warehouses={warehouses}
                  selectedWarehouse={selectedWarehouse}
                />
          </TabPane>*/}
        </Tabs>
      </Card>

      {/* 智能向導已移除 */}

      {/* 倉庫編輯模態框 */}
      <Modal
        title={editingWarehouse ? t('編輯倉庫') : t('新增倉庫')}
        open={warehouseModalVisible}
        onCancel={() => setWarehouseModalVisible(false)}
        onOk={() => {
          warehouseForm.validateFields().then(async values => {
            const computedAvailable = Math.max((values.capacityTotal || 0) - (values.capacityUsed || 0) - (values.capacityReserved || 0), 0);
            if (editingWarehouse) {
              try {
                await apiService.updateWarehouse(editingWarehouse.id, {
                  name: values.name,
                  code: values.code,
                  type: values.type,
                  capacityTotal: Number(values.capacityTotal || 0),
                  capacityUnit: values.capacityUnit,
                  capacityUsed: Number(values.capacityUsed || 0),
                  capacityReserved: Number(values.capacityReserved || 0),
                  temperatureRequirement: values.temperatureRequirement,
                });
                message.success(t('倉庫更新成功'));
                setWarehouseModalVisible(false);
                setEditingWarehouse(null);
                await loadWarehouses();
              } catch (e: any) {
                message.error(e?.message || t('更新失敗'));
              }
            } else {
              try {
                await apiService.createWarehouse({
                  name: values.name,
                  code: values.code,
                  type: values.type,
                  capacityTotal: Number(values.capacityTotal || 0),
                  capacityUnit: values.capacityUnit,
                  capacityUsed: Number(values.capacityUsed || 0),
                  capacityReserved: Number(values.capacityReserved || 0),
                  temperatureRequirement: values.temperatureRequirement,
                });
                message.success(t('倉庫創建成功'));
                setWarehouseModalVisible(false);
                await loadWarehouses();
              } catch (e: any) {
                message.error(e?.message || t('創建失敗'));
              }
            }
          });
        }}
        width={600}
      >
        <Form form={warehouseForm} layout="vertical">
          {/* 倉庫表單內容 */}
          <Form.Item label={t('倉庫名稱')} name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('倉庫代碼')} name="code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item label={t('倉庫類型')} name="type" rules={[{ required: true, message: t('請選擇倉庫類型') }]}>
            <Select placeholder={t('選擇倉庫類型')}>
              <Option value="smart">{t('智慧倉')}</Option>
              <Option value="automated">{t('自動化')}</Option>
              <Option value="traditional">{t('傳統')}</Option>
              <Option value="cold_storage">{t('冷鏈')}</Option>
              <Option value="hazmat">{t('危化')}</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('總容量')} name="capacityTotal" rules={[{ required: true, message: t('請輸入總容量') }]}>
                <InputNumber min={0} style={{ width: '100%' }} placeholder={t('輸入總容量')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('單位')} name="capacityUnit" rules={[{ required: true, message: t('請選擇容量單位') }]}>
                <Select placeholder={t('選擇單位')}>
                  <Option value="cbm">{t('立方米 (cbm)')}</Option>
                  <Option value="sqm">{t('平方米 (sqm)')}</Option>
                  <Option value="pallets">{t('棧板數 (pallets)')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('已用容量')} name="capacityUsed">
                <InputNumber min={0} style={{ width: '100%' }} placeholder={t('默認 0')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('預留容量')} name="capacityReserved">
                <InputNumber min={0} style={{ width: '100%' }} placeholder={t('默認 0')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={t('溫度要求')} name="temperatureRequirement">
            <Select placeholder={t('選擇溫度要求')} allowClear>
              <Option value="room_temperature">{t('常溫 (15-25°C)')}</Option>
              <Option value="cold">{t('冷藏 (2-8°C)')}</Option>
              <Option value="frozen">{t('冷凍 (-18°C以下)')}</Option>
              <Option value="controlled_room_temp">{t('控制室溫 (20-25°C)')}</Option>
              <Option value="cool">{t('陰涼 (8-15°C)')}</Option>
              <Option value="ultra_cold">{t('超低溫 (-80°C以下)')}</Option>
              <Option value="dry">{t('乾燥 (RH<60%)')}</Option>
              <Option value="humid_controlled">{t('濕度控制 (45-65% RH)')}</Option>
            </Select>
          </Form.Item>

          {/* 其他表單項... */}
        </Form>
      </Modal>

      {/* 倉庫詳情（只讀） */}
      <Modal
        title={selectedWarehouse ? t('倉庫詳情') : t('倉庫詳情')}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>{t('關閉')}</Button>
        ]}
        width={600}
      >
        <Form form={warehouseForm} layout="vertical" disabled>
          <Form.Item label={t('倉庫名稱')} name="name">
            <Input />
          </Form.Item>
          <Form.Item label={t('倉庫代碼')} name="code">
            <Input />
          </Form.Item>
          <Form.Item label={t('倉庫類型')} name="type">
            <Select>
              <Option value="smart">{t('智慧倉')}</Option>
              <Option value="automated">{t('自動化')}</Option>
              <Option value="traditional">{t('傳統')}</Option>
              <Option value="cold_storage">{t('冷鏈')}</Option>
              <Option value="hazmat">{t('危化')}</Option>
            </Select>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('總容量')} name="capacityTotal">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('單位')} name="capacityUnit">
                <Select>
                  <Option value="cbm">{t('立方米 (cbm)')}</Option>
                  <Option value="sqm">{t('平方米 (sqm)')}</Option>
                  <Option value="pallets">{t('棧板數 (pallets)')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('已用容量')} name="capacityUsed">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('預留容量')} name="capacityReserved">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label={t('溫度要求')} name="temperatureRequirement">
            <Select allowClear>
              <Option value="room_temperature">{t('常溫 (15-25°C)')}</Option>
              <Option value="cold">{t('冷藏 (2-8°C)')}</Option>
              <Option value="frozen">{t('冷凍 (-18°C以下)')}</Option>
              <Option value="controlled_room_temp">{t('控制室溫 (20-25°C)')}</Option>
              <Option value="cool">{t('陰涼 (8-15°C)')}</Option>
              <Option value="ultra_cold">{t('超低溫 (-80°C以下)')}</Option>
              <Option value="dry">{t('乾燥 (RH<60%)')}</Option>
              <Option value="humid_controlled">{t('濕度控制 (45-65% RH)')}</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 編碼規則編輯模態框 */}
      <Modal
        title={editingRule ? t('編輯編碼規則') : t('新增編碼規則')}
        open={codingRuleModalVisible}
        onCancel={() => {
          setCodingRuleModalVisible(false);
          setEditingRule(null);
          codingForm.resetFields();
        }}
        onOk={() => {
          codingForm.validateFields().then(async (values) => {
            try {
              // 後端需要的 payload 結構
              const examples = generateLocationPreview({ ...values, pattern: values.pattern } as LocationCodingRule, 5);
              const payload = {
                name: values.name,
                description: values.description,
                warehouseId: values.warehouseId,
                isDefault: !!values.isDefault,
                isActive: values.isActive !== false,
                pattern: values.pattern,
                examples,
              };

              if (editingRule) {
                await apiService.updateCodingRule(editingRule.id, payload);
                message.success(t('編碼規則更新成功！'));
              } else {
                await apiService.createCodingRule(payload);
                message.success(t('編碼規則創建成功！'));
              }
              setCodingRuleModalVisible(false);
              setEditingRule(null);
              codingForm.resetFields();
              await loadCodingRules();
            } catch (err: any) {
              const msg = err?.message || t('創建規則失敗');
              message.error(msg);
            }
          });
        }}
        width={800}
        destroyOnClose
      >
        <Form form={codingForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                label={t('規則名稱')} 
                name="name" 
                rules={[{ required: true, message: t('請輸入規則名稱') }]}
              >
                <Input placeholder={t('例如：標準編碼規則')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                label={t('適用倉庫')} 
                name="warehouseId" 
                rules={[{ required: true, message: t('請選擇適用倉庫') }]}
              >
                <Select placeholder={t('選擇倉庫')}>
                  {warehouses.map(warehouse => (
                    <Option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            label={t('規則描述')} 
            name="description" 
            rules={[{ required: true, message: t('請輸入規則描述') }]}
          >
            <Input.TextArea 
              rows={3} 
              placeholder={t('描述此編碼規則的適用場景和特點')}
            />
          </Form.Item>

          <Divider orientation="left">{t('編碼格式配置')}</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item 
                label={t('區域格式')} 
                name={['pattern', 'zone', 'type']} 
                rules={[{ required: true }]}
              >
                <Select onChange={(value) => {
                  if (value === 'custom') {
                    codingForm.setFieldsValue({
                      'pattern.zone.format': 'CUSTOM_LIST',
                      'pattern.zone.customList': ['REC', 'STO', 'PCK']
                    });
                  }
                }}>
                  <Option value="letter">{t('字母 (A-Z)')}</Option>
                  <Option value="number">{t('數字 (1-9)')}</Option>
                  <Option value="custom">{t('自定義列表')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                label={t('區域範圍')} 
                name={['pattern', 'zone', 'format']} 
                rules={[{ required: true }]}
              >
                <Input placeholder={t('例如：A-H 或 1-9')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                label={t('通道格式')} 
                name={['pattern', 'aisle', 'type']} 
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="letter">{t('字母 (A-Z)')}</Option>
                  <Option value="number">{t('數字 (01-99)')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item 
                label={t('通道範圍')} 
                name={['pattern', 'aisle', 'format']} 
                rules={[{ required: true }]}
              >
                <Input placeholder={t('例如：A-Z 或 01-99')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                label={t('貨架範圍')} 
                name={['pattern', 'rack', 'format']} 
                rules={[{ required: true }]}
              >
                <Input placeholder={t('例如：001-999')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                label={t('貨架長度')} 
                name={['pattern', 'rack', 'length']} 
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={10} placeholder="3" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item 
                label={t('層範圍')} 
                name={['pattern', 'shelf', 'format']} 
                rules={[{ required: true }]}
              >
                <Input placeholder={t('例如：01-10')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                label={t('層長度')} 
                name={['pattern', 'shelf', 'length']} 
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={3} placeholder="2" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                label={t('儲位範圍')} 
                name={['pattern', 'bin', 'format']} 
                rules={[{ required: true }]}
              >
                <Input placeholder={t('例如：01-20')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item 
                label={t('儲位長度')} 
                name={['pattern', 'bin', 'length']} 
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={3} placeholder="2" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                label={t('分隔符')} 
                name={['pattern', 'separator']} 
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="-">{t('連字符 (-)')}</Option>
                  <Option value="">{t('無分隔符')}</Option>
                  <Option value=".">{t('點號 (.)')}</Option>
                  <Option value="_">{t('下劃線 (_)')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                label={t('自定義區域列表')} 
                name={['pattern', 'zone', 'customList']}
                dependencies={[['pattern', 'zone', 'type']]}
              >
                {({ getFieldValue }) => {
                  const zoneType = getFieldValue(['pattern', 'zone', 'type']);
                  return zoneType === 'custom' ? (
                    <Select mode="tags" placeholder={t('輸入自定義區域代碼')}>
                      {['REC', 'STO', 'PCK', 'SHP'].map(item => (
                        <Option key={item} value={item}>{item}</Option>
                      ))}
                    </Select>
                  ) : null;
                }}
              </Form.Item>
            </Col>
          </Row>

          {/* 溫度控制配置區塊已移除（不再需要區域溫度設置） */}

          <Divider orientation="left">{t('規則設置')}</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                label={t('設為預設規則')} 
                name="isDefault" 
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                label={t('啟用規則')} 
                name="isActive" 
                valuePropName="checked"
              >
                <Switch defaultChecked />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <style>{`
        .selected-template {
          box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3) !important;
        }
      `}</style>
    </div>
  );
};

export default WarehouseManagement;